import React, { useEffect, useState } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';
import { forEach } from 'lodash';

import Tabs from '../../../app/components/Tabs';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { HarmfulFactorSearch } from '../../components/HarmfulFactor/HarmfulFactorSearch';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  GetHarmfulFactorDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  harmfulFactorData,
  validate,
} from '../../../common/spdCore/contexts/HarmfulFactorContext';
import { CreateUpdateHarmfulFactorDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateHarmfulFactorDto';
import { HarmfulFactorEditor } from '../../components/HarmfulFactor/HarmfulFactorEditor';
import { ReactSelectOption } from '../../components/Selects/SortableSelect';
import ValidationAlert from '../../../app/components/ValidationAlert';

export const HarmfulFactorsView: React.FC = () => {
  const spd = useSpdCore();
  const [show, setShow] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [harmfulFactor, setHarmfulFactor] = useState<GetHarmfulFactorDto>();
  const [allTests, setAllTests] = useState<ReactSelectOption[]>([]);
  const [tests, setTests] = useState<ReactSelectOption[]>([]);

  useEffect(() => {
    let mounted = true;
    spd.tests
      .getAll()
      .then(t => {
        if (mounted) {
          const result = t.map(
            a =>
              ({
                value: a.id,
                label: a.name,
              }) as ReactSelectOption,
          );
          setAllTests(result);
        }
      })
      .catch(error =>
        console.log(`Wystąpił problem z pobraniem testów. ${error}`),
      );

    return () => {
      mounted = false;
    };
  }, [spd.tests]);

  const handleAdd = () => {
    setShow(true);
    setTests([]);
    setHarmfulFactor(undefined);
  };

  const handleClose = () => {
    setShow(false);
    setRefresh(!refresh);
  };

  const addEditHarmfulFactor = async (
    hfCu: CreateUpdateHarmfulFactorDto,
    id?: string,
  ) => {
    const status = await validate(hfCu);
    if (status.valid) {
      try {
        if (id) {
          await spd.harmfulFactors.update(id, hfCu);
        } else {
          await spd.harmfulFactors.create(hfCu);
        }
        setShow(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const onRowClick = async (id: string) => {
    const getHF = await spd.harmfulFactors.get(id);
    const tests: ReactSelectOption[] = [];
    forEach(getHF.harmfulFactorTests, value => {
      tests.push({
        value: value.test.id,
        label: value.test.name || '',
      });
    });
    setTests(tests);
    setHarmfulFactor(getHF);
    setShow(true);
  };

  return (
    <Card>
      <Tabs defaultActiveKey="search" id="harmfulFactors" className="mb-3">
        <Tab eventKey="search" title="Czynniki szkodliwe" className="m-3">
          <ValidationAlert
            show={true}
            errors={[
              'Uwaga! Dodawanie nowego czynnika  szkodliwego i usuwanie istniejącego powinno być uzgodnione z modułem testów!',
            ]}
            className="mt-3"
          />
          <HarmfulFactorSearch
            getData={spd.harmfulFactors.getAllByPortion}
            getCount={spd.harmfulFactors.getAllCount}
            refresh={refresh}
            onRowClick={onRowClick}
          />
          <Button variant="outline-primary" onClick={handleAdd}>
            Nowy czynnik szkodliwy
          </Button>
          <HarmfulFactorEditor
            id={harmfulFactor?.id}
            harmfulFactor={harmfulFactorData(harmfulFactor)}
            onCompleted={addEditHarmfulFactor}
            show={show}
            handleClose={handleClose}
            allTests={allTests}
            selectedTests={tests}
            supportsExaminationDicts={harmfulFactor?.harmfulFactorSupports?.map(
              cb => cb.supportingExaminationDict,
            )}
          />
        </Tab>
      </Tabs>
    </Card>
  );
};
